import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { LockSimpleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';

type AgingBasis = 'days_overdue' | 'days_since_invoice' | 'days_since_shipment';

export interface ArReportSettingsValue {
  basis: AgingBasis;
  minDays: number;
  maxDays: number | undefined;
  /** When true, restrict to not-yet-due invoices. Only valid for
   *  basis="days_overdue" — ship and invoice dates rarely sit in the future. */
  currentOnly?: boolean;
}

interface ArReportSettingsProps {
  value: ArReportSettingsValue;
  onChange: (value: ArReportSettingsValue) => void;
  /**
   * Replace the interactive basis toggle with a read-only indicator showing
   * the (org-locked) basis. The user can see which basis is in effect but
   * cannot change it.
   */
  lockBasis?: boolean;
  /** Hide the min/max days inputs */
  hideDaysRange?: boolean;
  /** Label shown above the settings group */
  label?: string;
}

export function ArReportSettings({
  value,
  onChange,
  lockBasis = false,
  hideDaysRange = false,
  label = 'Report Settings',
}: ArReportSettingsProps) {
  const gt = useGT();
  const BASIS_OPTIONS: { value: AgingBasis; label: string }[] = [
    {
      value: 'days_overdue',
      label: gt('Due Date', { context: 'AR report aging basis option' }),
    },
    {
      value: 'days_since_invoice',
      label: gt('Invoice Date', { context: 'AR report aging basis option' }),
    },
    {
      value: 'days_since_shipment',
      label: gt('Ship Date', { context: 'AR report aging basis option' }),
    },
  ];

  function handleBasisChange(newBasis: string) {
    if (newBasis) {
      // Clear currentOnly when leaving the due-date basis — for ship/invoice
      // date bases the reference date rarely sits in the future, so the flag
      // wouldn't match anything and would just confuse users.
      const next: ArReportSettingsValue = {
        ...value,
        basis: newBasis as AgingBasis,
      };
      if (newBasis !== 'days_overdue') {
        next.currentOnly = false;
      }
      onChange(next);
    }
  }

  function handleCurrentOnlyChange(checked: boolean) {
    onChange({ ...value, currentOnly: checked });
  }

  const showCurrentToggle = value.basis === 'days_overdue';
  const daysInputsDisabled = value.currentOnly === true;

  function handleMinDaysChange(raw: string) {
    const parsed = raw ? parseInt(raw, 10) : 0;
    const newMin = Math.max(0, isNaN(parsed) ? 0 : parsed);
    onChange({ ...value, minDays: newMin });
  }

  function handleMaxDaysChange(raw: string) {
    if (!raw) {
      onChange({ ...value, maxDays: undefined });
      return;
    }
    const parsed = parseInt(raw, 10);
    onChange({
      ...value,
      maxDays: isNaN(parsed) ? undefined : Math.max(0, parsed),
    });
  }

  return (
    <div className="space-y-2">
      {label && <p className="text-sm font-medium">{label}</p>}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">
            <T>Basis</T>
          </Label>
          {lockBasis ? (
            <div
              className="flex h-9 items-center gap-1.5 rounded-md border bg-muted/40 px-3 text-sm text-muted-foreground"
              title={gt('Date basis is set by your organization settings', {
                context:
                  'tooltip explaining the AR report basis is locked by org config',
              })}
            >
              <LockSimpleIcon className="h-3.5 w-3.5" />
              <span>
                {BASIS_OPTIONS.find((opt) => opt.value === value.basis)
                  ?.label ?? value.basis}
              </span>
            </div>
          ) : (
            <ToggleGroup
              type="single"
              value={value.basis}
              onValueChange={handleBasisChange}
              size="sm"
            >
              {BASIS_OPTIONS.map((opt) => (
                <ToggleGroupItem
                  key={opt.value}
                  value={opt.value}
                  className="px-3"
                >
                  {opt.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
        </div>
        {!hideDaysRange && (
          <>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                <T>Min Days</T>
              </Label>
              <Input
                type="number"
                min={0}
                value={value.minDays || ''}
                placeholder="0"
                disabled={daysInputsDisabled}
                onChange={(e) => handleMinDaysChange(e.target.value)}
                className="w-24 h-8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                <T>Max Days</T>
              </Label>
              <Input
                type="number"
                min={0}
                value={value.maxDays ?? ''}
                placeholder={gt('No limit', {
                  context: 'max days input placeholder',
                })}
                disabled={daysInputsDisabled}
                onChange={(e) => handleMaxDaysChange(e.target.value)}
                className="w-24 h-8"
              />
            </div>
            {showCurrentToggle && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  <T>Current only</T>
                </Label>
                <label className="flex items-center gap-2 h-8 cursor-pointer">
                  <Checkbox
                    checked={value.currentOnly === true}
                    onCheckedChange={(checked) =>
                      handleCurrentOnlyChange(checked === true)
                    }
                  />
                  <span className="text-xs text-muted-foreground">
                    <T context="AR report filter — restrict to not-yet-due invoices">
                      Not yet due
                    </T>
                  </span>
                </label>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
